/**
 * CohesionMeter - each kill-team's Cohesion at the head of the order strip (Squad Mode stage 7, canvas board 7): a
 * 38 px pill banded in the team's livery, COHESION, the pool as cells (a bar past 15) and "n/start". A press opens a
 * popover (portalled: the strip clips what overflows it) with the Oath, the starting breakdown, what runs - each with
 * Stop for anyone in Squad Mode and the GM - and the GM's -/+.
 *
 * Which meters show is `metersFor` (the brother you act as: his; the GM: every kill-team; else none). Nothing outside
 * the table. It never shrinks: the strip's seats scroll instead.
 */
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useKillTeamsContext, type KillTeamsValue } from '../../contexts/KillTeamsContext';
import RosterContext from '../../contexts/RosterContext';
import SessionContext from '../../contexts/SessionContext';
import useEscapeKey from '../../hooks/useEscapeKey';
import { metersFor, meterShape, type Meter } from '../../engine/killTeam/meter';
import { liveryByCombatant } from '../../engine/roster/team';
import { liveryStyle } from '../../utils/liveryStyle';
import type { Combatant } from '../../engine/roster/types';
import type { Livery } from '../../engine/roster/team';
import styles from '../../styles/CohesionMeter.module.css';

interface Seat {
  roster: Combatant[];
  actorId: string | null;
  isGm: boolean;
}

function Cells({ pool, start }: { pool: number; start: number }) {
  const shape = meterShape(pool, start);
  if (shape.kind === 'bar') {
    return (
      <span className={styles.bar} aria-hidden="true">
        <span className={styles.barFill} style={{ width: `${Math.round(shape.fraction * 100)}%` }} />
      </span>
    );
  }
  return (
    <span className={styles.cells} aria-hidden="true">
      {Array.from({ length: shape.total }, (_, i) => (
        <span key={i} className={styles.cell} data-on={i < shape.filled || undefined} />
      ))}
    </span>
  );
}

/** Closes on Escape and on a press anywhere outside the pill and the popover. */
function useDismiss(
  open: boolean,
  close: () => void,
  a: RefObject<HTMLElement | null>,
  b: RefObject<HTMLElement | null>,
) {
  useEscapeKey(open, close);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: Event) => {
      if (![a, b].some((r) => r.current?.contains(e.target as Node))) close();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open, close, a, b]);
}

function Running({ meter, k, canStop }: { meter: Meter; k: KillTeamsValue; canStop: boolean }) {
  if (!meter.pool.running.length) return <span className={styles.says}>Nothing running.</span>;
  return (
    <ul className={styles.running}>
      {meter.pool.running.map((r) => (
        <li key={r.key} data-testid={`cohesion-running-${r.key}`}>
          <span>
            {r.name}
            <span className={styles.says}>{r.sustained ? ' · sustained' : ' · one-shot'}</span>
          </span>
          {canStop && (
            <button
              type="button"
              className={styles.step}
              data-testid={`cohesion-stop-${r.key}`}
              onClick={() => void k.actions?.stop(meter.tag, r.key)}
            >
              Stop
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

/** The starting Cohesion as the sheets work it out now, term by term. */
function Breakdown({ meter, k }: { meter: Meter; k: KillTeamsValue }) {
  const start = k.teams.killTeams[meter.tag]?.start;
  return (
    <>
      {start?.breakdown.map((line, i) => (
        <div key={line.label} className={styles.line}>
          <span>{line.label}</span>
          <strong>{i > 0 && line.value > 0 ? `+${line.value}` : line.value}</strong>
        </div>
      ))}
      <div className={styles.line}>
        <span>Starting</span>
        <strong>{meter.start}</strong>
      </div>
    </>
  );
}

/** The GM's -/+ on the pool, and Refill (plan modes-rework: the pool fills itself only once per session). */
function GmSteps({ meter, k }: { meter: Meter; k: KillTeamsValue }) {
  return (
    <div className={styles.gm}>
      <span>
        Now <strong>{meter.pool.pool}</strong> · GM
      </span>
      <button
        type="button"
        className={styles.step}
        aria-label="Spend one Cohesion"
        data-testid="cohesion-down"
        onClick={() => void k.actions?.adjust(meter.tag, -1)}
      >
        −
      </button>
      <button
        type="button"
        className={styles.step}
        aria-label="Restore one Cohesion"
        data-testid="cohesion-up"
        onClick={() => void k.actions?.adjust(meter.tag, 1)}
      >
        +
      </button>
      <button
        type="button"
        className={styles.step}
        title={`Back to its start, ${meter.start} (a new mission in the same session)`}
        data-testid="cohesion-refill"
        onClick={() => void k.actions?.refill(meter.tag)}
      >
        Refill
      </button>
    </div>
  );
}

function Popover(props: {
  meter: Meter;
  k: KillTeamsValue;
  seat: Seat;
  at: DOMRect;
  boxRef: RefObject<HTMLDivElement | null>;
}) {
  const { meter, k, seat, at, boxRef } = props;
  const actor = seat.roster.find((c) => c.id === seat.actorId);
  const canStop = seat.isGm || !!actor?.state.killTeam?.on;
  return createPortal(
    <div
      ref={boxRef}
      className={styles.popover}
      style={{ top: at.bottom + 4, left: at.left }}
      role="dialog"
      aria-label={`${meter.label}, Cohesion`}
      data-testid="cohesion-popover"
    >
      <span className={styles.caption}>{`${meter.label} · ${meter.oathName}`}</span>
      <Breakdown meter={meter} k={k} />
      <Running meter={meter} k={k} canStop={canStop} />
      {seat.isGm && <GmSteps meter={meter} k={k} />}
    </div>,
    document.body,
  );
}

function OneMeter({
  meter,
  k,
  seat,
  livery,
}: {
  meter: Meter;
  k: KillTeamsValue;
  seat: Seat;
  livery: Livery | undefined;
}) {
  const [at, setAt] = useState<DOMRect | null>(null);
  const pill = useRef<HTMLButtonElement | null>(null);
  const box = useRef<HTMLDivElement | null>(null);
  // One identity: the listeners hold across a busy table's re-renders while the popover is open (the stage review).
  const close = useCallback(() => setAt(null), []);
  useDismiss(!!at, close, pill, box);
  return (
    <>
      <button
        ref={pill}
        type="button"
        className={styles.pill}
        style={liveryStyle(livery)}
        aria-expanded={!!at}
        aria-label={`${meter.label} Cohesion, ${meter.pool.pool} of ${meter.start}`}
        data-testid={`cohesion-meter-${meter.tag}`}
        onClick={() => setAt(at ? null : (pill.current?.getBoundingClientRect() ?? null))}
      >
        <span className={styles.caption}>COHESION</span>
        <Cells pool={meter.pool.pool} start={meter.start} />
        <strong className={styles.count}>{`${meter.pool.pool}/${meter.start}`}</strong>
      </button>
      {at && <Popover meter={meter} k={k} seat={seat} at={at} boxRef={box} />}
    </>
  );
}

/** Every meter this viewer sees, side by side at the strip's head. */
export default function CohesionMeters() {
  const k = useKillTeamsContext();
  const rosterCtx = useContext(RosterContext) as { roster?: Combatant[]; actorId?: string | null } | null;
  const session = useContext(SessionContext) as { isGm?: boolean; sessionId?: string | null } | null;
  if (!k || !rosterCtx?.roster) return null;
  // A table with no session is its GM's.
  const seat: Seat = {
    roster: rosterCtx.roster,
    actorId: rosterCtx.actorId ?? null,
    isGm: !!session?.isGm || !session?.sessionId,
  };
  const meters = metersFor({ ...seat, teams: k.teams, cohesion: k.cohesion });
  if (!meters.length) return null;
  return <Meters meters={meters} k={k} seat={seat} />;
}

/** The meters, each in its team's livery (worked out once per roster). */
function Meters({ meters, k, seat }: { meters: Meter[]; k: KillTeamsValue; seat: Seat }) {
  const liveries = useMemo(() => liveryByCombatant(seat.roster), [seat.roster]);
  return (
    <>
      {meters.map((m) => (
        <OneMeter key={m.tag} meter={m} k={k} seat={seat} livery={liveries[m.brotherId]} />
      ))}
    </>
  );
}
